/* eslint-disable no-unused-vars */
/** @module Auth0PostLoginHandlerShipStationHome */
/* eslint-disable import/extensions */
import Auth0Constants from '../../../../utils/auth0-constants.mjs';
import Auth0CookieHelper from '../../../../utils/auth0-cookie-helper.mjs';
import Auth0LocalStorageHelper from '../../../../utils/auth0-local-storage-helper.mjs';
import Auth0UtilsBrowser from '../../../../utils/auth0-utils-browser.mjs';
import { Auth0NewUserOnboardingHelper } from '../../../auth0-new-user-onboarding-helper.mst.mjs';
import Auth0PostLoginHandlerBase from '../auth0-post-login-handler-base.mst.mjs';
import Auth0UserSessionHelper from '../../../../utils/auth0-session-helper.mjs';
import Auth0YofiTelemetryHelper from '../../../../utils/auth0-yofi-telemetry-helper.mjs';
import Auth0TokenStorageHelper from '../auth0-token-storage-helper.mjs';
import logger from '../../../../utils/logging/auth0-logger.mjs';

class Auth0PostLoginHandlerShipStationHome extends Auth0PostLoginHandlerBase {
  constructor(options) {
    super(options);
    this._initLogger('Auth0PostLoginHandlerShipStationHome');
  }

  /**
   * @inheritdoc
   * Returns true for ShipStation tenant class apps that need multi-partition
   * redirect logic, new user onboarding, cookie promotion, etc.
   *
   * Uses tenant-based detection instead of client ID list so new SS apps
   * automatically get the full post-login logic without code changes.
   */
  async shouldHandle(loginData) {
    // Use tenant class to determine if this is a ShipStation app
    // this._tenant is set by Auth0PostLoginHandlerBase from tenantOrDomainResolver
    const isShipStation = this._tenant?.isInTenantClass('shipstation') ?? false;
    if (!isShipStation) return Promise.resolve(false);

    // Defer to Account Linking handler for both phases of the linking flow:
    // 1. Unlinked SSO user (sub does not start with 'auth0|', no seller_id) — initial linking UI
    // 2. Linking completion (userInitiatedLinkingCompletion in appState) — after SS login proves ownership
    const appState = loginData?.appState ?? {};
    const additionalState = typeof appState.additionalState === 'string'
      ? (() => { try { return JSON.parse(appState.additionalState); } catch { return {}; } })()
      : (appState.additionalState ?? {});

    if (appState.userInitiatedLinkingCompletion || additionalState.userInitiatedLinkingCompletion) {
      console.log('Auth0PostLoginHandlerShipStationHome.shouldHandle() => false (linking completion, deferring to Account Linking)');
      return Promise.resolve(false);
    }

    const userSub = loginData?.user?.sub ?? '';
    const isLinkedSsUser = userSub.startsWith(Auth0Constants.AUTH0_ID_PREFIX) || !!Auth0UserSessionHelper.userSellerId;
    if (!isLinkedSsUser) {
      console.log('Auth0PostLoginHandlerShipStationHome.shouldHandle() => false (unlinked SSO user, deferring to Account Linking)');
      return Promise.resolve(false);
    }

    console.log(`Auth0PostLoginHandlerShipStationHome.shouldHandle() => true (tenant: ${this._tenant?.id})`);
    return Promise.resolve(true);
  }

  /**
   * @inheritdoc
   */
  async run(loginData) {
    console.log('Auth0PostLoginHandlerShipStationHome.run()...');

    // Store tokens — use app-specified tokenStorageConfig if provided, else default (_lat/_latv3)
    if (loginData.tokens) {
      if (this._tokenStorageConfig) {
        Auth0TokenStorageHelper.storeTokens(loginData.tokens, this._tokenStorageConfig);
      } else {
        Auth0TokenStorageHelper.storeTokensDefault(loginData.tokens);
      }
    }

    // first login??
    await this._maybeHandleFirstLoginActions(loginData);

    let calculatedRedirectUrl = this._buildPostLoginSsRedirectUrl(loginData);
    calculatedRedirectUrl = await this._preRedirectActions(calculatedRedirectUrl);

    console.log(`calculatedRedirectUrl => ${calculatedRedirectUrl}`);
    // pre-redirect state dump
    // Auth0UtilsBrowser.dumpVars('pre-sshome-redirect');
    // window.open(calculatedRedirectUrl, '_blank', 'noopener');
    window.location.href = calculatedRedirectUrl;
  }

  _buildPostLoginSsRedirectUrl = (userData) => {
    // Based on user data from auth0, derive where user should redirect
    if (!userData.user) {
      // console.log('no user found!');
      return;
    }
    console.log('found authed user =>', JSON.stringify(userData));

    const { redirectOverride, targetUrl: rawTargetUrl } = userData.appState || {};
    console.log(`SSHome._buildPostLoginSsRedirectUrl: appState=${JSON.stringify(userData.appState)}`);
    console.log(`SSHome._buildPostLoginSsRedirectUrl: redirectOverride=${redirectOverride}, targetUrl=${rawTargetUrl}`);

    // Only ever honor a targetUrl that shares the current page's base domain (any subdomain of
    // it is fine). This CDN script only ever runs on a domain we control, so window.location's
    // base domain is a trust anchor an attacker can't forge — reject anything else outright
    // (e.g. ?targetUrl=https://shipstation.evil.com used as a phishing lure) rather than trying
    // to enumerate every legitimate SS host pattern.
    const targetUrl = (rawTargetUrl && Auth0UtilsBrowser.isUrlOnCurrentBaseDomain(rawTargetUrl))
      ? rawTargetUrl
      : undefined;
    if (rawTargetUrl && !targetUrl) {
      console.warn(`SSHome._buildPostLoginSsRedirectUrl: rejecting targetUrl on foreign domain => ${rawTargetUrl}`);
    }
    // Redirect override? (SS help center, etc)
    if (redirectOverride) {
      console.log(`SSHome._buildPostLoginSsRedirectUrl: returning redirectOverride=${redirectOverride}`);
      return redirectOverride;
    }

    // Sysadmins and drop-in sessions navigate by explicit intent — preferred app is irrelevant.
    // Honor the targetUrl exactly as supplied by the caller (ss-ui-node already computed the
    // environment-correct host from req.hostname). Skip all preferred-app logic below.
    if (!this._shouldRedirectToUserHome(userData.user)) {
      const targetHost = Auth0UtilsBrowser.getUrlHostname(targetUrl) || window.location.hostname;
      console.log(`SSHome._buildPostLoginSsRedirectUrl: sysadmin/drop-in — honoring targetUrl directly (host=${targetHost})`);
      return this._buildRedirectUrlFromTargetUrl(targetUrl, targetHost, userData.user) || window.location.origin;
    }

    // If login originated from the SSL client, always redirect to SSL — never check preferred app.
    const loggedInFromSsLiteClient = this._ssOptions.clientId === 'sJTqpWRNoIw6bSLkihGXC7ozYwnE0XAI';

    // For v3 client logins, check preferred app to determine destination.
    // Falls back to null — no claim means honor partition_host as-is (standard SSv3/V2 behavior).
    const preferredSsAppId = loggedInFromSsLiteClient
      ? Auth0Constants.AUTH0_APP_ID_SS_LITE
      : (Auth0UtilsBrowser.getUserClaim('preferred_ss_app_id', userData.user) ?? null);


    // SS Lite destination: redirect to the correct SS Lite host.
    // Triggered by SSL client login (always) or v3 user with preferred_ss_app_id = AUTH0_APP_ID_SS_LITE.
    // partition_host contains shipN (v3) or ssN (v2) — both must be translated to their simple-*/lite-*
    // equivalents. ss-ui-node detects Lite context via subdomain only.
    if (preferredSsAppId === Auth0Constants.AUTH0_APP_ID_SS_LITE) {
      let partitionHost = Auth0UtilsBrowser.getUserClaim('partition_host', userData.user);
      partitionHost = this._specialHomePartitionDevFilters(partitionHost);
      // In local dev use lite-local subdomain to avoid colliding with the real lite.shipstation.com
      // (which is a CNAME for lite2 in prod). isLocalDevEnv() is a runtime hostname check — never
      // true in prod. Use sslocal.com TLD when running in a ZDE/DDE context.
      const localBaseDomain = window.location.hostname.endsWith('.sslocal.com') ? 'sslocal.com' : 'shipstation.com';

      // Translate v3/v2 partition hostname to its SS Lite equivalent.
      // Uses a first-match-wins ordered list identical in structure to ss-ui-node's
      // HOSTNAME_TRANSLATION_PAIRS so the two repos stay in sync.
      //   v3 stage: ship0 → simple-intg, ship1 → simple-stage
      //   v2 stage: ss0   → simple-intg, ss1   → simple-stage
      //   v3/v2 prod numbered: shipN/ssN → simple (bare)
      //   v3/v2 prod bare: ship/ss → simple
      //   ZDE: v3-devssNNN/ship-devssNNN → lite-devssNNN
      // On no match fall back to the targetUrl hostname (already the correct lite host when the
      // caller — e.g. ss-ui-node — computed it from its own request context).
      const _toLiteHost = (h) => {
        const LITE_PAIRS = [
          [/^ship0(\..+)$/, 'simple-intg$1'],
          [/^ss0(\..+)$/, 'simple-intg$1'],
          [/^ship1(\..+)$/, 'simple-stage$1'],
          [/^ss1(\..+)$/, 'simple-stage$1'],
          [/^ship\d+(\..+)$/, 'simple$1'],
          [/^ss\d+(\..+)$/, 'simple$1'],
          [/^ship(\..+)$/, 'simple$1'],
          [/^ss(\..+)$/, 'simple$1'],
          [/^(?:v3|ship)-devss(\d+)(\..+)$/, 'lite-devss$1$2'],
        ];
        const match = LITE_PAIRS.find(([pattern]) => pattern.test(h));
        if (match) return h.replace(match[0], match[1]);
        // No matching translation — return null so the caller can fall back safely.
        return null;
      };

      const targetUrlHost = Auth0UtilsBrowser.getUrlHostname(targetUrl);
      const liteHost = Auth0UtilsBrowser.isLocalDevEnv()
        ? `lite-local.${localBaseDomain}`
        // Translate partition_host → lite equivalent. Fall back to targetUrl's host
        // (already correct when set by ss-ui-node) rather than a hardcoded production host.
        : (partitionHost && _toLiteHost(partitionHost)) || targetUrlHost || `simple.${localBaseDomain}`;

      console.log(`SSHome._buildPostLoginSsRedirectUrl (lite): partitionHost=${partitionHost}, liteHost=${liteHost}, targetUrl=${targetUrl}`);
      return this._buildRedirectUrlFromTargetUrl(targetUrl, liteHost, userData.user) || window.location.origin;
    }

    // Otherwise, derive redirect assuming SS app targetUrl
    let partitionHost = Auth0UtilsBrowser.getUserClaim('partition_host', userData.user);
    partitionHost = this._specialHomePartitionDevFilters(partitionHost);
    console.log(`actual user redirect host => ${partitionHost}`);

    // 1.) On a partition that is not your "home"? Maybe replace the hostname
    partitionHost = this._shouldRedirectToUserHome(userData.user)
      ? partitionHost
      : (Auth0UtilsBrowser.getUrlHostname(targetUrl) || window.location.hostname);
    console.log(`derived user redirect host => ${partitionHost}`);

    // 2.) final redirect URL based on the intended target URL
    const redirectUrl = this._buildRedirectUrlFromTargetUrl(targetUrl, partitionHost, userData.user);

    console.log(`SSHome._buildPostLoginSsRedirectUrl: returning redirectUrl=${redirectUrl}`);
    return redirectUrl || window.location.origin;
  };

  _specialHomePartitionDevFilters = (homePartition) => {
    if (!homePartition) {
      return window.location.hostname;
    }
    homePartition = this._specialFilterForDisableRedirectToV3(homePartition);
    homePartition = Auth0UtilsBrowser.specialRedirectFilterForDevEnv(homePartition);
    return homePartition;
  };

  _specialFilterForDisableRedirectToV3 = (homePartition) => {
    return this._ssOptions.disableRedirectToV3 && Auth0UtilsBrowser.isHostnameForV3(homePartition) ? window.location.hostname : homePartition;
  };

  _shouldRedirectToUserHome = (user) => {
    return (!Auth0UtilsBrowser.isLocalDevEnv() && !Auth0UtilsBrowser.isSysAdmin(user) && !Auth0UtilsBrowser.isDropin());
  };

  _buildRedirectUrlFromTargetUrl = (targetUrl, userPartitionHost, user) => {
    targetUrl = this._getValidTargetUrl(targetUrl, userPartitionHost, user).href;

    // /api/ targetUrls (e.g. IntegratedApp OAuth-accept callbacks) are backend endpoints whose
    // host was already computed correctly by the caller — never swap in the user's partition host.
    if (this._isApiTargetUrl(targetUrl)) {
      return targetUrl;
    }

    if (!Auth0UtilsBrowser.isUrlForSsApp(targetUrl)) {
      return targetUrl;
    }
    const replaceThisHostname = Auth0UtilsBrowser.getUrlHostname(targetUrl);
    let redirectUrl = Auth0UtilsBrowser.urlReplace(targetUrl, replaceThisHostname, userPartitionHost);
    if (Auth0UtilsBrowser.isUrlForV3(redirectUrl)) {
      redirectUrl = Auth0UtilsBrowser.urlReplace(redirectUrl, '3000', '2999');
    } else {
      redirectUrl = Auth0UtilsBrowser.urlReplace(redirectUrl, '2999', '3000');
    }
    return redirectUrl;
  };

  _getValidTargetUrl(providedTargetUrl, userPartitionHost, user) {
    userPartitionHost = userPartitionHost || '';
    const defaultTargetUrl = window.location.origin;
    providedTargetUrl = providedTargetUrl || defaultTargetUrl;
    const isV3User = Auth0UtilsBrowser.isHostnameForV3(userPartitionHost);

    let url;
    try {
      const derivedTargetUrl = new URL(providedTargetUrl);

      // SS Lite users arriving with a v3 targetUrl is not a mismatch — the hostname
      // gets replaced with the lite equivalent by the caller. Only flag v2↔v3 conflicts.
      const isLiteUser = Auth0UtilsBrowser.isUrlForSsLite(`https://${userPartitionHost}`);
      // /api/ targetUrls (e.g. IntegratedApp OAuth-accept callbacks) are backend endpoints,
      // not UI page navigations — the user's app-version partition is irrelevant to them,
      // so they're exempt from the v2/v3 mismatch check.
      // NOTE: this exemption was added under duress to unblock a real incident (Klaviyo OAuth-accept
      // callbacks getting discarded). The more correct fix is likely to revisit whether this whole
      // v2/v3 mismatch guardrail should still exist at all — it was built to force users onto v3, and
      // there is no longer a concerted effort doing that, so the guardrail itself may be obsolete.
      const hasSSVersionMismatch = !isLiteUser && !this._isApiTargetUrl(derivedTargetUrl.href) && (
        (isV3User && Auth0UtilsBrowser.isUrlForV2(providedTargetUrl))
        || (!isV3User && Auth0UtilsBrowser.isUrlForV3(providedTargetUrl))
      );

      // Pass the user object so isSysAdmin can read the role_set claim. Without it,
      // getUserClaim('role_set', undefined) always returns null → isSupportAgent is always false.
      const isSupportAgent = (Auth0UtilsBrowser.isDropin() || Auth0UtilsBrowser.isSysAdmin(user));

      const shouldUseDefaultUrl = hasSSVersionMismatch
      && Auth0UtilsBrowser.isUrlForSsApp(providedTargetUrl)
      && !isSupportAgent;

      if (shouldUseDefaultUrl) {
        // console.log(`Incompatible target url for user on partition: ${userPartitionHost}; target: ${derivedTargetUrl}`);
        url = new URL(defaultTargetUrl);
      } else {
        url = this._isAuth0LoginUrl(derivedTargetUrl)
          ? new URL(derivedTargetUrl.origin)
          : derivedTargetUrl;
      }

      // If no path is provided, use the app provided default
      url = this._isProvidedTargetPathEmpty(url)
        ? new URL(`${url.origin}${this._ssOptions.defaultRedirectPath || '/'}`)
        : url;

      return url;
    } catch (err) {
      const redirectErr = new Error('ShipStation redirect handler has failed!', err);
      redirectErr.defaultUrl = defaultTargetUrl;
      throw redirectErr;
    }
  }

  _isAuth0LoginUrl = (url) => {
    return url.href.match(/^http[s]?:\/\/.+\.com\/login/ig);
  };

  _isApiTargetUrl = (url) => {
    try {
      return new URL(url).pathname.startsWith('/api/');
    } catch {
      return false;
    }
  };

  _isProvidedTargetPathEmpty = (providedTargetUrl) => {
    if (!providedTargetUrl) {
      return true;
    }
    // if the redirect URL is just the site origin (root), it is considered empty
    try {
      const hrefVal = providedTargetUrl.href.replace(/\/+$/, '');
      return hrefVal === providedTargetUrl.origin;
    } catch (err) {
      return true;
    }
  };

  _maybeHandleFirstLoginActions = async (data) => {
    // if this is a new user auto-logging in for the first time, this will handle post-registration data processing
    // like google tag manager, hubspot, etc
    if (!Auth0UtilsBrowser.getOption({ cookieName: Auth0Constants.AUTH0_NEEDS_NEW_USER_ACTIONS })) {
      console.log('_maybeHandleFirstLoginActions => not required...');
      return;
    }

    console.log('_maybeHandleFirstLoginActions => running first login actions...');
    try {
      await Auth0NewUserOnboardingHelper.processNewUserRegistrationResponseActions({
        userData: data.user,
        registrationResponse: data.user?.idpProps?.user_metadata?.registration_response
          ? JSON.parse(data.user?.idpProps?.user_metadata?.registration_response)
          : undefined,
      });
    } catch (error) {
      // isolate any error here
      logger.error(`Unexpected error encountered whilst processing first time login actions => ${error.message || error}`);
      this._handledUnexpectedException(error);
    }

    Auth0CookieHelper.deleteCookie(Auth0Constants.AUTH0_NEEDS_NEW_USER_ACTIONS);

    // if first login, allow a tenant to override the first time redirect URL
    // if not defined, will redirect to user home partition
    data.appState.redirectOverride = this._ssOptions.firstLoginRedirectUrl;
    console.log(`_maybeHandleFirstLoginActions => handled first login, set redirect override to "${data.appState.redirectOverride}"`);
  };

  _preRedirectActions = async (calculatedRedirectUrl) => {
    if (this._ssOptions.enableRestoreAuth0LocalStorage) {
      // Auth0LocalStorageHelper.saveAuth0TokensToCookie();
      // modify redirect url by appending auth0 token data to it
      // (must capture token data BEFORE the automatic cross-subdomain logout safety — applied
      // by Auth0PostLoginHandlerHelper after run() — clears this origin's auth0-spa-js cache)
      calculatedRedirectUrl = Auth0LocalStorageHelper.appendAuth0TokensAsUrlFragment(calculatedRedirectUrl);
    }

    await Auth0YofiTelemetryHelper.setCookieWithAccessTokenSafe();
    logger.log(`User ${Auth0UserSessionHelper.userUserName} (${Auth0UserSessionHelper.userId} | ${Auth0UserSessionHelper.userSellerId}) has logged in successfully`);

    // [DEV] simulating multiple partitions on localhost? (requires hosts file modification and
    //       caddy serving on local[AUTH0_DEV_LOCAL_MULTI_PARTITION_TESTING_KEY].shipstation.com AND local.shipstation.com)
    if (Auth0UtilsBrowser.isLocalDevEnv() && window.location.hostname.includes(Auth0Constants.AUTH0_DEV_LOCAL_MULTI_PARTITION_TESTING_KEY)) {
      const devOverridePartitionHost = window.location.href.replace(Auth0Constants.AUTH0_DEV_LOCAL_MULTI_PARTITION_TESTING_KEY, '');
      console.log(`(DEV) simulated user redirect host => ${devOverridePartitionHost}`);
      return devOverridePartitionHost;
    }

    console.log(`SSHome._preRedirectActions: returning calculatedRedirectUrl=${calculatedRedirectUrl}`);
    return calculatedRedirectUrl;
  };
}

export default Auth0PostLoginHandlerShipStationHome;
